import * as THREE from "three";
import { WornSkins } from "../skins/wornSkins";
import { SKIN_LOOKS } from "../skins/looks";
import type { EnemyView } from "./enemyView";
import { COIN, EMBER, FLASH, GLINT, PING, PING_AT, PUFF, SPARK, SPARKLE, WISP, coinLanding, rnd } from "./killFxSpecs";

/**
 * Kill effects (MP-23, the Trophy Hall's `kill` cosmetics; v2 2026-10-21, "every skin an improvement over the base, with
 * its own animation"): what an enemy's death becomes when one is worn. Each is two beats — the body changes as it falls,
 * then it breaks apart as the dissolve starts:
 *
 * - Gilded Burst: a gold flash and a pop of stars as it dies; the falling body is gilded (a gold rim, gold cracks running
 *   out from the chest); when it dissolves the statue SHATTERS into a fountain of spinning coins that bounce on the road
 *   and wink as they land, star sparkles, a gold shock ring and a gold light pulse; gold glints climb the dissolve front.
 * - Ember Wisps: a hot puff and spat sparks; the body smoulders (a white-hot rim, a flickering ember burn, cinder cracks);
 *   as it dissolves it crumbles into embers that SPIRAL up off the dissolve front (vfx.js `swirl`), every few trailing a
 *   long faint wisp that curls up with them; a scorch mark is left on the road and an ember light flickers.
 *
 * Presentation only: EnemyViews calls `onKill` as a death starts (views/enemies.ts kill) and `onDying` each frame of it;
 * the sim never reads any of this (the goldens). The particles are the sprite VFX's own pool (art/vfx.js: recycled
 * particles, the quality tier's `lod`, the live cap, budget class `impact`), spawned from ONE reused spec object per
 * kind (killFxSpecs.ts), so a frame allocates nothing; per-view state is two small ints in WeakMaps. The body beats
 * drive the unit look's existing uniforms (unitFx.ts: rim heat, the boost rim, the burn flicker, the boss-death cracks),
 * which UnitLook.reset clears on the view's next spawn.
 *
 * Gates: Reduced effects / prefers-reduced-motion → no particles, no cracks (the dissolve edge and the body tint keep
 * the effect's colour: a colour, not motion); low tiers (lod ≤ 0.4) → the bursts thinned, no trail, light or mark; the
 * lowest (lod ≤ 0.25) → a few coins or embers. Bosses get the bursts (doubled) but keep their own cracks and no trail;
 * the Starfall Wyrm keeps its own fall; a rare keeps its own shine.
 */
export type KillStyle = "gilded" | "ember";

/** The sprite VFX surface this needs (art/vfx.js VFX: `spawn` copies the spec into a recycled particle). */
interface Spawner {
  spawn(spec: object): unknown; ring(pos: THREE.Vector3, opts: object): unknown; lod: number; anims: Readonly<Record<string, readonly string[]>>;
  decal?(pos: THREE.Vector3, opts: object): unknown;
  light?(pos: THREE.Vector3, color: readonly number[], radius: number, intensity: number, life: number, opts: object): unknown;
  readonly lights?: readonly { readonly on: boolean }[];
}

/** Tunables (world units, seconds, per-second rates). */
export const KILL_FX = {
  /** Gilded: stars in the death pop; coins, sparkles at the shatter (every `pingEvery`th coin winks as it lands); glints a second up the dissolving body. */
  pop: 4, coins: 9, sparkles: 10, pingEvery: 2, glintRate: 24,
  /** Ember: sparks spat at the death, embers in the puff; embers a second off the dissolve front, every `wispEvery`th trailing a wisp. */
  spit: 3, puff: 5, emberRate: 40, wispEvery: 3,
  /** The cracks reach the whole body by the shatter (at least this long). */
  crackMin: 0.2,
  /** At or under this `lod` (low tier) the per-frame trail, the light and the mark stop; at or under `lowest` the bursts keep a third. */
  low: 0.4, lowest: 0.25,
  /** A boss's bursts are this much bigger. */
  boss: 2,
} as const;

/** The dissolve edge (and the cracks) a style burns with (linear-ish hex, as unitFx's per-archetype edges). */
export const KILL_EDGE: Readonly<Record<KillStyle, number>> = { gilded: 0xffc84a, ember: 0xff6a12 };

const GOLD_RING = { size: 1.1, life: 0.45, color: [1, 0.82, 0.32], bright: 2.4, flat: true };
const GOLD_LIGHT = [1, 0.78, 0.35] as const, EMBER_LIGHT = [1, 0.42, 0.12] as const;
const PULSE = { flicker: false }, FLICKER = { flicker: true };
const SCORCH = { type: "scorch", size: 0.55, life: 1.8, hold: 0.5, color: [0.09, 0.07, 0.06], alpha: 0.55 };
const _at = new THREE.Vector3();

/** Per pooled view: emission owed between frames in thousandths of a particle, and the beat reached (0 died, 1 shattered). Small ints: no boxing. */
const owed = new WeakMap<EnemyView, number>();
const beat = new WeakMap<EnemyView, number>();
const MILLI = 1000;
let emitted = 0;

export class KillFx {
  /** The worn kill effect's style, or null (none worn: the stock death). */
  static style(): KillStyle | null {
    const id = WornSkins.get().kill ?? null;
    return id ? SKIN_LOOKS[id]?.kill ?? null : null;
  }

  /** A death starts (`v` at its last pose): the edge colour and the body's first beat, and the death pop unless effects are reduced. */
  static onKill(v: EnemyView, fx: object | null, reduced: boolean): void {
    const style = v.wyrm ? null : KillFx.style();
    v.look?.tintEdge(style ? KILL_EDGE[style] : null);
    owed.set(v, 0);
    beat.set(v, 0);
    if (!style) return;
    KillFx.bodyOnKill(v, style);
    if (reduced || !fx) return;
    const s = fx as Spawner, k = burst(s, v);
    const c = centre(v, 0.5);
    if (style === "gilded") {
      FLASH.pos.copy(c); s.spawn(FLASH);
      for (let i = 0, m = count(KILL_FX.pop, k); i < m; i++) {
        SPARKLE.pos.set(c.x + rnd(-0.12, 0.12), c.y + rnd(-0.1, 0.15), c.z + rnd(-0.12, 0.12));
        SPARKLE.vel.set(rnd(-0.6, 0.6), rnd(0.8, 1.4), rnd(-0.6, 0.6)); SPARKLE.life = rnd(0.35, 0.55); SPARKLE.rot = rnd(0, 6.3); SPARKLE.rotVel = rnd(-3, 3);
        s.spawn(SPARKLE);
      }
    } else {
      PUFF.pos.copy(c); s.spawn(PUFF);
      for (let i = 0, m = count(KILL_FX.spit, k); i < m; i++) {
        SPARK.pos.copy(c); SPARK.vel.set(rnd(-1.4, 1.4), rnd(1.4, 2.4), rnd(-1.4, 1.4)); SPARK.life = rnd(0.35, 0.6); SPARK.floor = v.root.position.y + 0.03;
        s.spawn(SPARK);
      }
      for (let i = 0, m = count(KILL_FX.puff, k); i < m; i++) KillFx.ember(s, v, rnd(0.2, 0.8), i % KILL_FX.wispEvery === 0);
    }
  }

  /** Each frame of a death: the body's beat, the shatter as the dissolve starts, then the trail off the dissolving body. */
  static onDying(v: EnemyView, fx: object | null, dt: number, reduced: boolean): void {
    if (v.wyrm || !v.look) return;
    const style = KillFx.style();
    if (!style) return;
    const d = v.look.dissolve;
    if (!reduced && !v.boss && v.rare <= 0) v.look.crack = Math.min(1, v.dead / Math.max(KILL_FX.crackMin, v.dissolveAt));
    if (reduced || !fx) return;
    const s = fx as Spawner;
    if (d > 0 && beat.get(v) === 0) { beat.set(v, 1); KillFx.shatter(v, s, style); }
    if (v.boss || s.lod <= KILL_FX.low || d <= 0 || d >= 1) return;
    const rate = (style === "gilded" ? KILL_FX.glintRate : KILL_FX.emberRate) * Math.min(1, s.lod);
    let due = (owed.get(v) ?? 0) / MILLI + rate * dt;
    const p = v.root.position, h = v.bodyH * v.scale;
    for (; due >= 1; due -= 1) {
      const front = Math.min(1, d + rnd(0, 0.25));      // the dissolve climbs from the feet: the particles leave its front
      if (style === "ember") KillFx.ember(s, v, front, ++emitted % KILL_FX.wispEvery === 0);
      else {
        GLINT.pos.set(p.x + rnd(-0.14, 0.14) * v.scale, p.y + h * front, p.z + rnd(-0.14, 0.14) * v.scale);
        GLINT.vel.set(0, rnd(0.2, 0.5), 0); GLINT.rot = rnd(0, 6.3);
        s.spawn(GLINT);
      }
    }
    owed.set(v, Math.round(due * MILLI));
  }

  /** The body's beat as it dies: Gilded's gold flash and gold rim; Ember's white-hot rim and smouldering burn. Colours: kept under reduced effects. */
  private static bodyOnKill(v: EnemyView, style: KillStyle): void {
    const look = v.look;
    if (!look) return;
    if (style === "gilded") { look.flash(0.7); look.target.w = 1; }             // the boost rim is the game's gold rim
    else { look.heatUp(1); look.target.y = 1; }                                   // the burn: a flickering ember glow, weighted to the feet
  }

  /** The second beat, as the dissolve starts: the gilded statue bursts into coins; the smouldering body leaves its mark and its light. */
  private static shatter(v: EnemyView, s: Spawner, style: KillStyle): void {
    const k = burst(s, v), p = v.root.position, c = centre(v, 0.45);
    const rich = s.lod > KILL_FX.low && !v.boss;
    if (style === "gilded") {
      FLASH.pos.copy(c); s.spawn(FLASH);
      if (s.lod > KILL_FX.low) s.ring(_at.set(p.x, p.y + 0.02, p.z), GOLD_RING);
      COIN.anim = s.anims.coinspin ? "coinspin" : null;
      const floor = p.y + 0.06;
      for (let i = 0, m = count(KILL_FX.coins, k); i < m; i++) {
        COIN.pos.copy(c);
        COIN.vel.set(rnd(-1.15, 1.15), rnd(2.4, 3.5), rnd(-1.15, 1.15));
        COIN.animRate = rnd(14, 20); COIN.rotVel = rnd(-8, 8); COIN.life = rnd(1.0, 1.3); COIN.floor = floor;
        s.spawn(COIN);
        if (rich && i % KILL_FX.pingEvery === 0) {                    // a wink where it lands (spawned now, dark until then: no timer)
          const t = coinLanding(COIN.vel.y, c.y - floor);
          PING.pos.set(c.x + COIN.vel.x * t * 0.9, floor + 0.04, c.z + COIN.vel.z * t * 0.9);
          PING.life = t / PING_AT; PING.rot = rnd(0, 6.3);
          s.spawn(PING);
        }
      }
      for (let i = 0, m = count(KILL_FX.sparkles, k); i < m; i++) {
        SPARKLE.pos.set(c.x + rnd(-0.25, 0.25), c.y + rnd(-0.2, 0.3), c.z + rnd(-0.25, 0.25));
        SPARKLE.vel.set(rnd(-0.3, 0.3), rnd(0.6, 1.2), rnd(-0.3, 0.3));
        SPARKLE.life = rnd(0.5, 1.0); SPARKLE.rot = rnd(0, 6.3); SPARKLE.rotVel = rnd(-2, 2);
        s.spawn(SPARKLE);
      }
      if (rich) light(s, c, GOLD_LIGHT, 1.4, 0.35, PULSE);
    } else {
      if (rich) { s.decal?.(_at.set(p.x, p.y, p.z), SCORCH); light(s, c, EMBER_LIGHT, 1.2, 0.7, FLICKER); }
      for (let i = 0, m = count(KILL_FX.puff, k); i < m; i++) KillFx.ember(s, v, rnd(0.05, 0.4), i % KILL_FX.wispEvery === 0);
    }
  }

  /** One ember at `at` of the body's height, spiralling up as it cools; with a wisp curling up after it. */
  private static ember(s: Spawner, v: EnemyView, at: number, wisp: boolean): void {
    const p = v.root.position, h = v.bodyH * v.scale, turn = Math.random() < 0.5 ? -1 : 1;
    const a = rnd(0, 6.283), r = rnd(0.5, 0.75);                    // a horizontal speed round the body: the helix's radius is speed / swirl
    EMBER.pos.set(p.x + rnd(-0.12, 0.12) * v.scale, p.y + h * at, p.z + rnd(-0.12, 0.12) * v.scale);
    EMBER.vel.set(Math.cos(a) * r, rnd(0.55, 1.0), Math.sin(a) * r);
    EMBER.swirl = turn * rnd(4, 6); EMBER.life = rnd(0.9, 1.5); EMBER.size0 = rnd(0.07, 0.12);
    s.spawn(EMBER);
    if (!wisp) return;
    WISP.pos.copy(EMBER.pos); WISP.vel.copy(EMBER.vel).multiplyScalar(0.8);
    WISP.swirl = EMBER.swirl; WISP.life = rnd(1.1, 1.6);
    s.spawn(WISP);
  }
}

/** A burst's scale: the tier's `lod` (a third at the lowest), doubled for a boss. */
function burst(s: Spawner, v: EnemyView): number {
  return Math.min(1, s.lod) * (s.lod <= KILL_FX.lowest ? 0.34 : 1) * (v.boss ? KILL_FX.boss : 1);
}
/** `base` particles at scale `k`, at least one. */
const count = (base: number, k: number): number => Math.max(1, Math.round(base * k));

/** The body's centre (at `frac` of its height), wherever its death has thrown it (a launched body flies off its feet). */
const _c = new THREE.Vector3();
function centre(v: EnemyView, frac: number): THREE.Vector3 {
  const p = v.root.position, b = v.body.position;
  return _c.set(p.x + b.x, p.y + Math.max(0, b.y) + v.bodyH * v.scale * frac, p.z + b.z);
}

/** A VFX light slot, only when one is free: a kill never steals the fortress strike's or a boss's light. */
function light(s: Spawner, at: THREE.Vector3, rgb: readonly number[], radius: number, life: number, opts: object): void {
  if (!s.light || !s.lights) return;
  for (let i = 0; i < s.lights.length; i++) if (!s.lights[i].on) { s.light(at, rgb, radius, 1.6, life, opts); return; }
}
